import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Search, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PRIORITY_META } from "./TaskBadges";
import TaskEntries from "./TaskEntries";

// FIELD-001C1B / C2B · Canonical resource-type catalogue.
const RESOURCE_TYPES = [
    "Electrician",
    "Panel Installer",
    "Scaffolder",
    "Groundworks Contractor",
    "General / Unspecified",
];

// FIELD-001C · Defensive fallbacks used when `api.taskMetadata()`
// hasn't loaded yet (Lead / field-user sessions where the bootstrap
// aborted on the Ops-only roster call, or first-open before the
// parent finishes fetching). Mirrors the backend's canonical
// `TASK_STATUSES` / `TASK_CATEGORIES` / `TASK_PRIORITIES` in
// `backend/core/tasks.py`. Kept intentionally small — the real
// catalogue always wins when it arrives.
const FALLBACK_STATUSES = ["Not Started", "In Progress", "Completed", "Cancelled"];
const FALLBACK_PRIORITIES = ["Low", "Normal", "High", "Critical"];
const FALLBACK_CATEGORIES = [
    "Survey", "Installation", "Electrical", "Commissioning",
    "Customer", "Procurement", "DNO", "Certification",
    "Administration", "Finance", "Warranty", "Planning", "General",
];

/**
 * TaskForm — unified create / edit modal for Task Management.
 *
 * Props:
 *   • open              — modal visibility
 *   • initial           — existing task doc (edit mode) or partial (create)
 *   • projects          — full installer project list
 *   • engineers         — engineer roster (active + archived)
 *   • metadata          — { categories, priorities, statuses }
 *   • lockedProjectId   — when set, hides the project search
 *   • enableMultiAssign — FIELD-001C2B · shows the multi-select
 *                          resource picker + required_resource_type
 *                          (Operations surface). When false, falls
 *                          back to the legacy single-select for
 *                          mobile / installer surfaces.
 *   • onClose(saved?)   — closes; refreshes parent if `saved` provided
 */
export default function TaskForm({
    open, initial, projects, engineers, metadata,
    lockedProjectId, enableMultiAssign = false, onClose,
}) {
    const isEdit = Boolean(initial?.id);
    const { user } = useAuth();
    // FIELD-001C · Permission-aware mode. Clicking an existing task
    // opens THIS same form (not a summary drawer), and every field is
    // enabled / submitted only when the caller is authorised to
    // change it. Server-side enforcement is authoritative — the
    // client contract is defensive UX, not the security boundary.
    //
    //   "create"          — new-task authoring (Ops or Lead within own
    //                       projects). Full multi-assign UI.
    //   "ops"             — Ops editing an existing task. Full UI.
    //   "lead_assign"     — Project Lead editing an unassigned
    //                       incomplete task in their own project.
    //                       Assignees + required-type editable; other
    //                       fields read-only.
    //   "assignee_status" — Personally assigned field user on an
    //                       incomplete task. Status editable; other
    //                       fields read-only. Submit sends `status`
    //                       only via the same PATCH endpoint.
    //   "readonly"        — Everyone else with visibility (completed
    //                       task, not-my-task, etc). No submit.
    const canManage = Array.isArray(user?.permissions)
        && user.permissions.includes("manage_projects");
    const myEngineerId = user?.engineer_id || null;
    const leadIds = Array.isArray(user?.leads_project_ids) ? user.leads_project_ids : [];
    const initialProjectId = initial?.project_id || lockedProjectId || null;
    const initialAssignees = useMemo(() => {
        if (Array.isArray(initial?.assigned_engineer_ids) && initial.assigned_engineer_ids.length) {
            return initial.assigned_engineer_ids;
        }
        return initial?.assigned_engineer_id ? [initial.assigned_engineer_id] : [];
    }, [initial]);
    const isMyAssignee = !!myEngineerId && initialAssignees.includes(myEngineerId);
    const isUnassignedTask = initialAssignees.length === 0;
    const isTerminal = initial?.status === "Completed" || initial?.status === "Cancelled";
    // FIELD-001C · Authoritative task doc fetch (see comment further
    // down). Declared here so `src` is initialised before the option
    // memos below.
    const [fullTask, setFullTask] = useState(null);
    const [loadingTask, setLoadingTask] = useState(false);
    useEffect(() => {
        if (!open || !isEdit) { setFullTask(null); return; }
        let cancelled = false;
        setLoadingTask(true);
        (async () => {
            try {
                const t = await api.getTask(initial.id);
                if (!cancelled) setFullTask(t);
            } catch { /* fall back to `initial` alone */ }
            finally { if (!cancelled) setLoadingTask(false); }
        })();
        return () => { cancelled = true; };
    }, [open, isEdit, initial?.id]);
    const src = useMemo(() => ({ ...(initial || {}), ...(fullTask || {}) }), [initial, fullTask]);
    // FIELD-001C · When the caller couldn't fetch the Ops-only
    // roster (Lead / field-user sessions), TaskForm falls back to
    // the project-scoped assignment options endpoint once a project
    // is selected. Backend gates that endpoint to Ops or the
    // Project Lead of that specific project — so this is still
    // server-authoritative, never a client widening.
    const [fallbackEngineers, setFallbackEngineers] = useState([]);
    // FIELD-001C · Reactive project id — while creating a task in the
    // standalone form the caller picks the project AFTER opening; the
    // Project Lead authority derivation therefore MUST follow the
    // current selection, not the (empty) initial one. `projectId`
    // state below is seeded from `initialProjectId` in the open-effect.
    const [projectId, setProjectId] = useState("");
    const currentProjectId = projectId || initialProjectId || null;
    const isMyProjectLead = !!myEngineerId && !!currentProjectId && leadIds.includes(currentProjectId);
    // FIELD-001C · Per-field authority (supersedes the earlier one
    // big "Ops-only edit" flag). Every field derives its own gate;
    // Lead + assignee union naturally when both apply.
    //
    // Task-authority rules (FIELD-001C, BUILD-11 correction):
    //   • Operations retains full task-management authority.
    //   • A Project Lead may create OR edit an ad-hoc task on a
    //     project they lead — with every legitimate field enabled.
    //     BUILD-11 UAT reproduced the earlier constraint: title /
    //     category / priority / due date / due time / estimated
    //     duration were disabled the moment the modal switched from
    //     create → edit for a Lead on their own ad-hoc task. The
    //     server-side authority ALWAYS allowed it (Lead PATCH honours
    //     every ad-hoc field), so the client-only `!isEdit` gate was
    //     a UX drift bug, not an authority weakening.
    //   • Canonical (workflow) tasks retain the read-only general
    //     contract — there is no canonical general-edit endpoint, so
    //     enabling those fields would let the user type changes that
    //     Save would silently discard.
    //   • Personally-assigned field users may update status + notes.
    //   • Everyone else is refused — server enforces independently.
    const isCanonicalTask    = (initial?.kind === "workflow");
    const canEditGeneral     = ((canManage || (isMyProjectLead && !isCanonicalTask)) && !isTerminal);
    const canEditDescription = (canManage || isMyProjectLead) && !isTerminal;
    const canEditAssignees   = (canManage || isMyProjectLead) && !isTerminal;
    const canEditStatus      = (canManage || isMyAssignee || (!isEdit && isMyProjectLead)) && !isTerminal;
    // FIELD-001C Section B · Contribution rights for attributed
    // notes/photos are permitted even on completed tasks so evidence
    // can be back-filled without reopening. Server enforces the same
    // predicate independently.
    const canContributeEntries = canManage || isMyProjectLead || isMyAssignee;
    const canSubmit = !isEdit
        ? (canManage || isMyProjectLead)
        : (canEditGeneral || canEditDescription || canEditAssignees || canEditStatus);
    // Derived "mode" — retained for the header chip only.
    let mode;
    if (!isEdit) {
        mode = (canManage || isMyProjectLead) ? "create" : "readonly";
    } else if (isTerminal) {
        mode = "readonly";
    } else if (canManage) {
        mode = "ops";
    } else if (isMyProjectLead && isMyAssignee) {
        mode = "lead_assignee";
    } else if (isMyProjectLead) {
        mode = "lead";
    } else if (isMyAssignee) {
        mode = "assignee_status";
    } else {
        mode = "readonly";
    }
    const multiAssign = canEditAssignees || mode === "create" || enableMultiAssign;
    const activeEngineers = useMemo(() => {
        // FIELD-001C · Guarantee every saved assignee id resolves to a
        // named row even when the caller can't fetch the full roster
        // (Leads / field users). Missing ids get a synthetic row with
        // the display name the backend already resolved on the task
        // payload — never render "Unassigned" for an existing
        // assignment we can't look up.
        const primary = (engineers || []).filter((e) => e.active !== false);
        const fallback = (fallbackEngineers || []).filter((e) => e.active !== false);
        const base = primary.length ? primary : fallback;
        const byId = new Map(base.map((e) => [e.id, e]));
        const ids = Array.isArray(src?.assigned_engineer_ids) && src.assigned_engineer_ids.length
            ? src.assigned_engineer_ids
            : (src?.assigned_engineer_id ? [src.assigned_engineer_id] : []);
        const names = Array.isArray(src?.assigned_engineer_names) && src.assigned_engineer_names.length
            ? src.assigned_engineer_names
            : (src?.assigned_engineer_name ? [src.assigned_engineer_name] : []);
        ids.forEach((id, i) => {
            if (!byId.has(id)) {
                byId.set(id, {
                    id, displayName: names[i] || id,
                    active: true, resourceType: null, trade: null,
                    __synthetic: true,
                });
            }
        });
        return [...byId.values()];
    }, [engineers, fallbackEngineers, src]);
    // Load the project-scoped assignment options when the parent
    // couldn't hand us a roster and the caller has picked a project.
    useEffect(() => {
        const parentEmpty = !Array.isArray(engineers) || engineers.length === 0;
        if (!open || !parentEmpty || !currentProjectId || !canEditAssignees) {
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await api.projectAssignmentOptions(currentProjectId);
                if (!cancelled) setFallbackEngineers(res?.options || []);
            } catch { if (!cancelled) setFallbackEngineers([]); }
        })();
        return () => { cancelled = true; };
    }, [open, engineers, currentProjectId, canEditAssignees]);
    // FIELD-001C · Metadata option lists always include the currently
    // saved value AND fall back to the canonical catalogue when the
    // caller's `/tasks/metadata` fetch hasn't (or couldn't) land.
    // `mergeOpts(source, fallback, saved)` returns a de-duplicated
    // list that always contains `saved` so a bare selector never
    // reads as blank.
    const mergeOpts = (loaded, fallback, saved) => {
        const arr = Array.isArray(loaded) && loaded.length ? [...loaded] : [...fallback];
        if (saved && !arr.includes(saved)) arr.unshift(saved);
        return arr;
    };
    const categoryOptions = useMemo(
        () => mergeOpts(metadata?.categories, FALLBACK_CATEGORIES, src?.task_category),
        [metadata, src],
    );
    const priorityOptions = useMemo(
        () => mergeOpts(metadata?.priorities, FALLBACK_PRIORITIES, src?.priority),
        [metadata, src],
    );
    const statusOptions = useMemo(() => {
        // FIELD-001C · Status is special: an assigned field user in
        // `assignee_status` mode must always see the current status
        // PLUS the permitted transitions. Backend evaluates the
        // WF-001A1 dependency rules on save — the UI mirror is
        // defensive. Cancelled is Ops-only per the current
        // permission model, so non-Ops callers never see it in the
        // selector.
        const base = mergeOpts(metadata?.statuses, FALLBACK_STATUSES, src?.status);
        const filtered = canManage ? base : base.filter((s) => s !== "Cancelled");
        if (src?.status) {
            const idx = filtered.indexOf(src.status);
            if (idx > 0) { filtered.splice(idx, 1); filtered.unshift(src.status); }
        }
        return filtered;
    }, [metadata, src, canManage]);;

    const [projectSearch, setProjectSearch] = useState("");
    const [showProjectList, setShowProjectList] = useState(false);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [category, setCategory] = useState("General");
    const [priority, setPriority] = useState("Normal");
    const [status, setStatus] = useState("Not Started");
    const [engineerId, setEngineerId] = useState("");
    const [engineerIds, setEngineerIds] = useState(() => new Set());
    const [engineerNames, setEngineerNames] = useState([]);
    const [requiredType, setRequiredType] = useState("");
    const [dueDate, setDueDate] = useState("");
    const [dueTime, setDueTime] = useState("");
    const [estimatedDuration, setEstimatedDuration] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    // FIELD-001M2 (correction) · Hydrate the form state EXACTLY ONCE
    // per "open cycle", with a second run when a fresh `fullTask`
    // lands from `GET /tasks/{id}` in edit mode. Previously this
    // effect depended on `[open, src, projects, metadata,
    // lockedProjectId]` — but `projects` is passed inline from
    // `InstallerTasks.jsx` as `taskProjects.length ? taskProjects : projects`,
    // producing a NEW ARRAY REFERENCE on every parent render, and
    // `metadata` similarly changes once the async bootstrap resolves.
    // A parent re-render therefore silently reset every form control
    // (project, dueDate, dueTime, estimatedDuration, title, …) —
    // including a valid `dueDate` the user had just typed into the
    // native `<input type="date">`. The behaviour reproduces
    // reliably in the live preview: the browser paints the typed
    // text, React state remains empty, `due-time` stays disabled
    // and the guidance never disappears.
    //
    // The fix is a ref-keyed guard: hydrate once when the modal
    // opens (`__new__` for creates), and hydrate again exactly when
    // the authoritative `fullTask` id arrives in edit mode. `projects`
    // and `metadata` are still read for the initial project display,
    // but a reference change from an unrelated parent render can
    // never wipe the form again. A separate small effect keeps the
    // display string in sync with the eventual `projects` load
    // without touching the rest of the form state.
    const hydratedKeyRef = useRef(null);
    useEffect(() => {
        if (!open) {
            hydratedKeyRef.current = null;
            return;
        }
        const key = fullTask?.id ? `full:${fullTask.id}` : (initial?.id ? `init:${initial.id}` : "__new__");
        if (hydratedKeyRef.current === key) return;
        hydratedKeyRef.current = key;
        setError("");
        const pid = src?.project_id || lockedProjectId || "";
        setProjectId(pid);
        // FIELD-001C · Existing-task project must always show its
        // authoritative customer identity — even when the caller
        // can't access the full project list. Fall back to the
        // task's decorated `project_customer_name`.
        const p = (projects || []).find((x) => x.id === pid);
        setProjectSearch(
            p ? `${p.customer_name} — ${p.address || ""}`.trim()
              : (src?.project_customer_name
                  ? `${src.project_customer_name}${src.project_address ? " — " + src.project_address : ""}`
                  : "")
        );
        setTitle(src?.title || "");
        setDescription(src?.description || "");
        setCategory(src?.task_category || metadata?.categories?.[0] || "General");
        setPriority(src?.priority || "Normal");
        setStatus(src?.status || "Not Started");
        setEngineerId(src?.assigned_engineer_id || "");
        const seed = Array.isArray(src?.assigned_engineer_ids) && src.assigned_engineer_ids.length
            ? src.assigned_engineer_ids
            : (src?.assigned_engineer_id ? [src.assigned_engineer_id] : []);
        setEngineerIds(new Set(seed));
        const names = Array.isArray(src?.assigned_engineer_names) && src.assigned_engineer_names.length
            ? src.assigned_engineer_names
            : (src?.assigned_engineer_name ? [src.assigned_engineer_name] : []);
        setEngineerNames(names);
        setRequiredType(src?.required_resource_type || "");
        setDueDate(src?.due_date || "");
        setDueTime(src?.due_time || "");
        setEstimatedDuration(src?.estimated_duration || "");
        // `projects` / `metadata` / `lockedProjectId` are intentionally
        // NOT in the dep list — they are read at hydration time but
        // must not force a re-hydrate. The `src?.id`/`fullTask?.id`
        // key already covers create → edit transitions, and the
        // separate effect below keeps the display string in sync
        // when `projects` finally lands.
    }, [open, fullTask, initial]); // eslint-disable-line react-hooks/exhaustive-deps

    // FIELD-001M2 (correction) · Fill the project display string
    // once when the projects list arrives — but only if the user
    // hasn't already typed a search AND we can identify the current
    // project. Never touches other form state.
    useEffect(() => {
        if (!open) return;
        if (!projectId) return;
        if (projectSearch.trim()) return;
        const p = (projects || []).find((x) => x.id === projectId);
        if (p) setProjectSearch(`${p.customer_name} — ${p.address || ""}`.trim());
    }, [open, projects, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

    // FIELD-001M2 · Clearing the due date must also clear the time
    // so the payload never carries a `due_time` without its
    // authoritative `due_date`.
    useEffect(() => {
        if (!dueDate && dueTime) setDueTime("");
    }, [dueDate]); // eslint-disable-line react-hooks/exhaustive-deps

    // FIELD-001C2B · Existing selections that would conflict with the
    // currently-selected required_resource_type. Surface inline so
    // Operations can resolve them before hitting Save (the backend
    // returns a structured 409 that we display verbatim if they don't).
    const conflicting = useMemo(() => {
        if (!multiAssign || !requiredType) return [];
        return [...engineerIds].filter((id) => {
            const e = activeEngineers.find((x) => x.id === id);
            if (!e) return false;
            const rt = (e.resourceType || e.trade || "").trim();
            return rt !== requiredType;
        });
    }, [multiAssign, requiredType, engineerIds, activeEngineers]);

    const rosterTypes = useMemo(() => {
        const s = new Set(RESOURCE_TYPES);
        for (const e of activeEngineers) {
            const rt = e.resourceType || e.trade;
            if (rt) s.add(rt);
        }
        return Array.from(s);
    }, [activeEngineers]);

    const filteredProjects = useMemo(() => {
        const needle = projectSearch.trim().toLowerCase();
        if (!needle) return (projects || []).slice(0, 20);
        return (projects || []).filter((p) => {
            const hay = [p.customer_name, p.address, p.system_summary, p.id]
                .map((x) => String(x || "").toLowerCase()).join(" ");
            return hay.includes(needle);
        }).slice(0, 30);
    }, [projects, projectSearch]);

    const canSave = canSubmit && !loadingTask && projectId && title.trim() && category
        && (!multiAssign || conflicting.length === 0);

    async function submit() {
        if (!canSave) { setError("Please complete required fields."); return; }
        setSaving(true);
        setError("");
        try {
            const idsArr = [...engineerIds];
            // FIELD-001C · Payload restricted to the fields the
            // caller is authorised to change. Server enforces the
            // same rules — the client contract is defensive.
            let body;
            if (!isEdit) {
                body = {
                    project_id: projectId,
                    title: title.trim(),
                    description: description || "",
                    task_category: category,
                    priority,
                    status,
                    due_date: dueDate || null,
                    due_time: dueDate && dueTime ? dueTime : null,
                    estimated_duration: estimatedDuration ? Number(estimatedDuration) : null,
                };
                if (multiAssign) {
                    body.assigned_engineer_ids = idsArr;
                    body.required_resource_type = requiredType || null;
                } else {
                    body.assigned_engineer_id = engineerId || null;
                }
            } else {
                // FIELD-001C · Existing-task PATCH — only include the
                // fields the caller is authorised to change. Lead and
                // assignee unions are already computed above; server
                // enforces the same rule and surfaces
                // FIELD_NOT_PERMITTED on drift.
                body = {};
                if (canEditGeneral) {
                    body.title = title.trim();
                    body.task_category = category;
                    body.priority = priority;
                    body.due_date = dueDate || null;
                    body.due_time = dueDate && dueTime ? dueTime : null;
                    body.estimated_duration = estimatedDuration ? Number(estimatedDuration) : null;
                }
                if (canEditDescription) body.description = description || "";
                if (canEditAssignees) {
                    body.assigned_engineer_ids = idsArr;
                    body.required_resource_type = requiredType || null;
                }
                if (canEditStatus) body.status = status;
            }
            const saved = isEdit ? await api.updateTask(initial.id, body) : await api.createTask(body);
            toast.success(isEdit ? "Task updated" : "Task created");
            onClose?.(saved);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const msg = (typeof detail === "object" ? detail?.message : null)
                     || formatApiError(detail)
                     || "Failed to save task";
            // FIELD-001C2B · On API failure, retain every user input so
            // Operations can correct + retry without re-entering the form.
            setError(msg);
        } finally { setSaving(false); }
    }

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 bg-black/55 flex items-end md:items-center justify-center md:px-4"
            data-testid="task-form-modal"
        >
            <div className="bg-white w-full md:max-w-2xl md:rounded-2xl md:shadow-xl overflow-hidden max-h-[92vh] flex flex-col">
                <div className="px-5 md:px-6 py-4 border-b border-[#E5E7EB] flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-[#131B2F] flex-1" data-testid="task-form-title-heading">
                        {isEdit ? "Task" : "New task"}
                    </h3>
                    {isEdit && (
                        <span
                            data-testid="task-form-mode"
                            data-mode={mode}
                            className="text-[11px] uppercase tracking-[0.08em] text-[#4B5565]"
                        >
                            {mode === "ops" && "Edit"}
                            {mode === "lead_assignee" && "Lead + Update"}
                            {mode === "lead" && "Lead"}
                            {mode === "assignee_status" && "Update status"}
                            {mode === "readonly" && "View"}
                        </span>
                    )}
                    <button
                        onClick={() => onClose?.()}
                        className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#F5F5F7] text-[#4B5565]"
                        aria-label="Close"
                        data-testid="task-form-close"
                    >
                        <X size={18} strokeWidth={1.75} />
                    </button>
                </div>

                <div className="px-5 md:px-6 py-4 overflow-y-auto space-y-4 flex-1">
                    {/* Project search — hidden when locked to a project */}
                    {!lockedProjectId && (
                        <div>
                            <label className="text-sm font-medium text-[#131B2F] block mb-1">Project *</label>
                            <div className="relative">
                                <div className="flex items-center gap-2 px-3 py-2.5 border border-[#D1D5DB] rounded-md bg-white">
                                    <Search size={14} strokeWidth={1.75} className="text-[#4B5565]" />
                                    <input
                                        type="text"
                                        value={projectSearch}
                                        onChange={(e) => { setProjectSearch(e.target.value); setShowProjectList(true); }}
                                        onFocus={() => setShowProjectList(true)}
                                        placeholder="Search by customer, postcode, address…"
                                        data-testid="task-form-project-search"
                                        className="flex-1 min-w-0 outline-none text-sm bg-transparent"
                                        disabled={isEdit}
                                    />
                                </div>
                                {showProjectList && !isEdit && (
                                    <div className="absolute z-10 left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-white border border-[#E5E7EB] rounded-md shadow-lg" data-testid="task-form-project-list">
                                        {filteredProjects.length === 0 && (
                                            <div className="px-3 py-2 text-sm text-[#4B5565]">No matching projects</div>
                                        )}
                                        {filteredProjects.map((p) => (
                                            <button
                                                key={p.id}
                                                type="button"
                                                onClick={() => {
                                                    setProjectId(p.id);
                                                    setProjectSearch(`${p.customer_name} — ${p.address || ""}`.trim());
                                                    setShowProjectList(false);
                                                }}
                                                data-testid={`task-form-project-option-${p.id}`}
                                                className="w-full text-left px-3 py-2 text-sm hover:bg-[#F5F5F7] border-b border-[#F3F4F6] last:border-b-0"
                                            >
                                                <div className="font-medium text-[#131B2F]">{p.customer_name}</div>
                                                <div className="text-xs text-[#4B5565] truncate">{p.address}</div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="text-sm font-medium text-[#131B2F] block mb-1">Title *</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            disabled={!canEditGeneral}
                            data-testid="task-form-title"
                            className="w-full px-3 py-2.5 border border-[#D1D5DB] rounded-md text-sm bg-white disabled:bg-[#F5F5F7] disabled:text-[#4B5565]"
                        />
                    </div>

                    <div>
                        <label className="text-sm font-medium text-[#131B2F] block mb-1">Description</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={2}
                            disabled={!canEditDescription}
                            data-testid="task-form-description"
                            className="w-full px-3 py-2.5 border border-[#D1D5DB] rounded-md text-sm bg-white resize-y disabled:bg-[#F5F5F7] disabled:text-[#4B5565]"
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="text-sm font-medium text-[#131B2F] block mb-1">Category *</label>
                            <select
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                disabled={!canEditGeneral}
                                data-testid="task-form-category"
                                className="w-full px-3 py-2.5 border border-[#D1D5DB] rounded-md text-sm bg-white disabled:bg-[#F5F5F7] disabled:text-[#4B5565]"
                            >
                                {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-[#131B2F] block mb-1">Priority</label>
                            <div className="flex flex-wrap gap-1" data-testid="task-form-priority">
                                {priorityOptions.map((p) => {
                                    const m = PRIORITY_META[p] || { bg: "#EEF2FF", fg: "#3730A3", dot: "#3730A3" };
                                    const on = priority === p;
                                    return (
                                        <button
                                            key={p}
                                            type="button"
                                            onClick={() => canEditGeneral && setPriority(p)}
                                            disabled={!canEditGeneral}
                                            data-testid={`task-form-priority-${p}`}
                                            aria-pressed={on}
                                            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium border ${on ? "" : "border-[#D1D5DB] bg-white text-[#131B2F]"} disabled:cursor-not-allowed`}
                                            style={on ? { background: m.bg, color: m.fg, borderColor: m.dot } : {}}
                                        >
                                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.dot }} />
                                            {p}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            {multiAssign ? (
                                <>
                                    <label className="text-sm font-medium text-[#131B2F] block mb-1">Required resource type</label>
                                    <select
                                        value={requiredType}
                                        onChange={(e) => setRequiredType(e.target.value)}
                                        disabled={!canEditAssignees}
                                        data-testid="task-form-required-type"
                                        className="w-full px-3 py-2.5 border border-[#D1D5DB] rounded-md text-sm bg-white disabled:bg-[#F5F5F7] disabled:text-[#4B5565]"
                                    >
                                        <option value="">Any resource type</option>
                                        {rosterTypes.map((t) => (
                                            <option key={t} value={t}>{t}</option>
                                        ))}
                                    </select>
                                </>
                            ) : (
                                <>
                                    <label className="text-sm font-medium text-[#131B2F] block mb-1">Assigned resource</label>
                                    <select
                                        value={engineerId}
                                        onChange={(e) => setEngineerId(e.target.value)}
                                        disabled={!canEditAssignees}
                                        data-testid="task-form-engineer"
                                        className="w-full px-3 py-2.5 border border-[#D1D5DB] rounded-md text-sm bg-white disabled:bg-[#F5F5F7] disabled:text-[#4B5565]"
                                    >
                                        <option value="">Unassigned</option>
                                        {activeEngineers.map((e) => (
                                            <option key={e.id} value={e.id}>{e.displayName}</option>
                                        ))}
                                    </select>
                                </>
                            )}
                        </div>
                        <div>
                            <label className="text-sm font-medium text-[#131B2F] block mb-1">Status</label>
                            <select
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                disabled={!canEditStatus && isEdit}
                                data-testid="task-form-status"
                                className="w-full px-3 py-2.5 border border-[#D1D5DB] rounded-md text-sm bg-white disabled:bg-[#F5F5F7] disabled:text-[#4B5565]"
                            >
                                {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                    </div>

                    {multiAssign && (
                        <div>
                            <label className="text-sm font-medium text-[#131B2F] block mb-1">
                                Assigned resources ({engineerIds.size})
                            </label>
                            <div className="border border-[#D1D5DB] rounded-md max-h-48 overflow-y-auto divide-y divide-[#F3F4F6]"
                                 data-testid="task-form-multi-assign">
                                {activeEngineers.length === 0 && (
                                    <div className="px-3 py-2 text-xs text-[#4B5565]">No active resources.</div>
                                )}
                                {activeEngineers.map((e) => {
                                    const on = engineerIds.has(e.id);
                                    const rt = (e.resourceType || e.trade || "").trim();
                                    const mismatched = !!requiredType && rt !== requiredType;
                                    const disabled = !canEditAssignees || (mismatched && !on);
                                    return (
                                        <button
                                            key={e.id}
                                            type="button"
                                            disabled={disabled || saving}
                                            data-testid={`task-form-multi-opt-${e.id}`}
                                            data-mismatched={mismatched ? "true" : undefined}
                                            aria-pressed={on}
                                            onClick={() => {
                                                setEngineerIds((prev) => {
                                                    const next = new Set(prev);
                                                    if (next.has(e.id)) next.delete(e.id); else next.add(e.id);
                                                    return next;
                                                });
                                            }}
                                            className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                                                on ? "bg-[#EAF6E1]" : disabled ? "opacity-50" : "hover:bg-[#F5F5F7]"
                                            } disabled:cursor-not-allowed`}
                                        >
                                            <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                                                on ? "bg-[#3E9E2E] border-[#3E9E2E] text-white" : "bg-white border-[#D1D5DB]"
                                            }`}>
                                                {on && <Check size={10} strokeWidth={2.5} />}
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm text-[#131B2F] truncate">
                                                    {e.displayName || e.organisationName || e.id}
                                                </div>
                                                <div className="text-[11px] text-[#4B5565] truncate">
                                                    {rt || "—"}{mismatched ? " · type mismatch" : ""}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                            {conflicting.length > 0 && (
                                <div className="mt-2 text-[11px] text-[#B45309]" data-testid="task-form-conflict">
                                    Selected resources don't match the required type — resolve before saving.
                                </div>
                            )}
                        </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                            <label className="text-sm font-medium text-[#131B2F] block mb-1">Due date</label>
                            <input
                                type="date"
                                value={dueDate}
                                // FIELD-001M2 (final correction) · The controlled
                                // `<input type="date">` under React 18 emits its
                                // synthetic `change` event only after the user
                                // COMMITS the picker value (e.g. blur, picker
                                // close). Some browsers and modal-portal
                                // combinations delay or drop that commit in edge
                                // cases, leaving the DOM painting the typed
                                // value while React state stays empty — exactly
                                // the divergence Codex UAT reproduces on the
                                // Field project entry point. Binding to `onInput`
                                // as well captures every keystroke and every
                                // picker selection immediately, so `dueDate`
                                // state can never lag the DOM.
                                onInput={(e) => setDueDate(e.target.value)}
                                onChange={(e) => setDueDate(e.target.value)}
                                onBlur={(e) => setDueDate(e.target.value)}
                                disabled={!canEditGeneral}
                                data-testid="task-form-due-date"
                                className="w-full px-3 py-2.5 border border-[#D1D5DB] rounded-md text-sm bg-white disabled:bg-[#F5F5F7] disabled:text-[#4B5565]"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-[#131B2F] block mb-1">Due time</label>
                            <input
                                type="time"
                                value={dueTime}
                                onInput={(e) => setDueTime(e.target.value)}
                                onChange={(e) => setDueTime(e.target.value)}
                                disabled={!dueDate || !canEditGeneral}
                                data-testid="task-form-due-time"
                                className="w-full px-3 py-2.5 border border-[#D1D5DB] rounded-md text-sm bg-white disabled:bg-[#F5F5F7]"
                            />
                            {canEditGeneral && !dueDate && (
                                <p className="mt-1 text-[11px] text-[#4B5565]" data-testid="task-form-due-time-hint">
                                    Choose a due date to set a time.
                                </p>
                            )}
                        </div>
                        <div>
                            <label className="text-sm font-medium text-[#131B2F] block mb-1">Est. minutes</label>
                            <input
                                type="number"
                                value={estimatedDuration}
                                onChange={(e) => setEstimatedDuration(e.target.value)}
                                min={0}
                                disabled={!canEditGeneral}
                                data-testid="task-form-duration"
                                className="w-full px-3 py-2.5 border border-[#D1D5DB] rounded-md text-sm bg-white disabled:bg-[#F5F5F7] disabled:text-[#4B5565]"
                            />
                        </div>
                    </div>

                    {isEdit && initial?.id && (
                        <TaskEntries
                            taskRef={initial.kind === "workflow"
                                ? `canonical:${initial.project_id}:${initial.canonical_task_id || (initial.id || "").replace(/^canon:[^:]+:/, "")}`
                                : `adhoc:${initial.id}`}
                            legacyNote={src?.notes || ""}
                            canContribute={canContributeEntries}
                        />
                    )}

                    {isEdit && loadingTask && (
                        <div data-testid="task-form-loading" className="text-xs text-[#4B5565]">Loading task…</div>
                    )}

                    {error && <div className="text-sm text-[#DA3535]">{error}</div>}
                </div>

                <div className="px-5 md:px-6 py-4 border-t border-[#E5E7EB] flex flex-wrap justify-end gap-2">
                    <button
                        onClick={() => onClose?.()}
                        data-testid="task-form-cancel"
                        className="px-4 py-2 rounded-md border border-[#D1D5DB] text-sm font-medium hover:bg-[#F5F5F7]"
                    >
                        {canSubmit ? "Cancel" : "Close"}
                    </button>
                    {canSubmit && (
                    <button
                        onClick={submit}
                        disabled={!canSave || saving}
                        data-testid="task-form-submit"
                        className="px-4 py-2 rounded-md bg-[#3E9E2E] text-white text-sm font-medium hover:bg-[#348426] disabled:opacity-50"
                    >
                        {saving ? "Saving…"
                            : !isEdit ? "Create task"
                            : mode === "assignee_status" ? "Update status"
                            : (mode === "lead" || mode === "lead_assignee") ? "Save changes"
                            : "Save changes"}
                    </button>
                    )}
                </div>
            </div>
        </div>
    );
}
